import React, { useEffect, useState } from "react";
import { supabase } from "../../supabaseClient";

export default function ProgramAdmin({ userId }) {
  const isUuid = (value) =>
    /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
      String(value || "")
    );
  const [programs, setPrograms] = useState([]);
  const [selectedProgramId, setSelectedProgramId] = useState(null);
  const [skillScores, setSkillScores] = useState([]);
  const [combineScores, setCombineScores] = useState([]);
  const [positions, setPositions] = useState([]);
  const [selectedProgramSportId, setSelectedProgramSportId] = useState("");
  const [developmentSections, setDevelopmentSections] = useState([]);
  const [performanceCategories, setPerformanceCategories] = useState([]);

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalType, setModalType] = useState("skill");
  const [currentDrillId, setCurrentDrillId] = useState(null);

  // Form State
  const [newDrillName, setNewDrillName] = useState("");
  const [newDrillTarget, setNewDrillTarget] = useState("");
  const [newDrillAttempts, setNewDrillAttempts] = useState("");
  const [newDrillDescription, setNewDrillDescription] = useState("");
  const [newDrillTimed, setNewDrillTimed] = useState(false);
  const [selectedPositionsForDrill, setSelectedPositionsForDrill] = useState([]);
  const [selectedSectionsForDrill, setSelectedSectionsForDrill] = useState([]);
  const [selectedCategoriesForDrill, setSelectedCategoriesForDrill] = useState([]);
  const [sectionPickerExpanded, setSectionPickerExpanded] = useState(true);
  const [categorySearch, setCategorySearch] = useState("");
  const [modalStep, setModalStep] = useState(1);
  const [isTipsModalOpen, setIsTipsModalOpen] = useState(false);

  const getDrillCategoryIds = (drill) => {
    if (Array.isArray(drill?.performance_categories) && drill.performance_categories.length > 0) {
      return drill.performance_categories;
    }
    // Backward compatibility: older rows may still store ids in development_items.
    if (Array.isArray(drill?.development_items) && drill.development_items.length > 0) {
      return drill.development_items;
    }
    return [];
  };
  const getCategorySectionId = (row) =>
    String(row?.development_section_id || row?.section_id || "").trim();

  // --- Fetch programs ---
  useEffect(() => {
    if (!userId) return;
    async function fetchPrograms() {
      const { data, error } = await supabase
        .from("programs")
        .select("*")
        .eq("administrator", userId);
      if (error) console.error(error);
      else {
        setPrograms(data || []);
        if (data && data.length > 0) setSelectedProgramId(data[0].id);
      }
    }
    fetchPrograms();
  }, [userId]);

  // --- Fetch positions ---
  useEffect(() => {
    async function fetchPositions() {
      const { data, error } = await supabase
        .from("positions")
        .select("*, sport:development_sports(name)");
      if (error) console.error(error);
      else setPositions(data || []);
    }
    fetchPositions();
  }, []);

  // --- Fetch drills ---
  useEffect(() => {
    if (!selectedProgramId) return;
    async function fetchDrills() {
      const { data: skills } = await supabase
        .from("program_goal_skill_scores")
        .select("*")
        .eq("program_id", selectedProgramId);
      setSkillScores(skills || []);

      const { data: combines } = await supabase
        .from("program_goal_combine_scores")
        .select("*")
        .eq("program_id", selectedProgramId);
      setCombineScores(combines || []);
    }
    fetchDrills();
  }, [selectedProgramId]);

  // --- Fetch development taxonomy (sections + performance categories) ---
  useEffect(() => {
    if (!selectedProgramId) {
      setSelectedProgramSportId("");
      setDevelopmentSections([]);
      setPerformanceCategories([]);
      return;
    }

    async function fetchDevelopmentTaxonomy() {
      const selectedProgram = programs.find((p) => String(p.id) === String(selectedProgramId));
      const rawSport = selectedProgram?.sport;
      if (!rawSport) {
        setSelectedProgramSportId("");
        setDevelopmentSections([]);
        setPerformanceCategories([]);
        return;
      }

      let sportId = "";
      if (isUuid(rawSport)) {
        sportId = rawSport;
      } else {
        const { data: sportLookup, error: sportError } = await supabase
          .from("development_sports")
          .select("id")
          .ilike("name", String(rawSport).trim())
          .limit(1)
          .maybeSingle();
        if (sportError || !sportLookup?.id) {
          setSelectedProgramSportId("");
          setDevelopmentSections([]);
          setPerformanceCategories([]);
          return;
        }
        sportId = sportLookup.id;
      }

      const [{ data: sectionRows, error: sectionError }] = await Promise.all([
        supabase
          .from("development_sections")
          .select("id, name")
          .eq("sport_id", sportId)
          .order("name", { ascending: true }),
      ]);
      let categoryRows = [];
      let categoryError = null;
      const { data: categoriesByDevelopmentId, error: categoriesByDevelopmentIdError } =
        await supabase
          .from("performance_categories")
          .select("id, name, development_section_id")
          .order("name", { ascending: true });
      if (!categoriesByDevelopmentIdError) {
        categoryRows = categoriesByDevelopmentId || [];
      } else {
        const { data: categoriesBySectionId, error: categoriesBySectionIdError } = await supabase
          .from("performance_categories")
          .select("id, name, section_id")
          .order("name", { ascending: true });
        if (!categoriesBySectionIdError) {
          categoryRows = (categoriesBySectionId || []).map((row) => ({
            ...row,
            development_section_id: row.section_id,
          }));
        } else {
          categoryError = categoriesBySectionIdError;
        }
      }

      if (sectionError || categoryError) {
        console.error(sectionError || categoryError || categoriesByDevelopmentIdError);
        setSelectedProgramSportId(sportId);
        setDevelopmentSections([]);
        setPerformanceCategories([]);
        return;
      }

      const allowedSectionIds = new Set((sectionRows || []).map((row) => String(row.id)));
      const scopedCategories = (categoryRows || []).filter((row) =>
        allowedSectionIds.has(getCategorySectionId(row))
      );

      setSelectedProgramSportId(sportId);
      setDevelopmentSections(sectionRows || []);
      setPerformanceCategories(scopedCategories);
    }

    fetchDevelopmentTaxonomy();
  }, [selectedProgramId, programs]);

  // --- Modal Logic ---
  const togglePositionForDrill = (positionId) => {
    setSelectedPositionsForDrill((prev) =>
      prev.includes(positionId) ? prev.filter((p) => p !== positionId) : [...prev, positionId]
    );
  };

  const toggleSectionForDrill = (sectionId) => {
    const nextSectionId = String(sectionId || "").trim();
    setSelectedSectionsForDrill((prev) => {
      const current = String(prev?.[0] || "").trim();
      const next = current === nextSectionId ? [] : [nextSectionId];
      const activeSection = next[0] || "";
      setSelectedCategoriesForDrill((categoriesPrev) =>
        (categoriesPrev || []).filter((categoryId) => {
          const category = performanceCategories.find((row) => String(row.id) === String(categoryId));
          if (!category) return false;
          return getCategorySectionId(category) === String(activeSection);
        })
      );
      return next;
    });
    setSectionPickerExpanded(false);
  };

  const toggleCategoryForDrill = (categoryId) => {
    setSelectedCategoriesForDrill((prev) =>
      prev.includes(categoryId) ? prev.filter((id) => id !== categoryId) : [...prev, categoryId]
    );
  };

  const openModal = (type, drill = null) => {
    setModalType(type);
    setCurrentDrillId(drill?.id || null);
    setSelectedPositionsForDrill(drill?.position_ids || []);
    setNewDrillName(drill?.skill_name || drill?.drill_name || "");
    setNewDrillTarget(drill?.target_score || "");
    setNewDrillAttempts(drill?.total_attempts ?? "");
    setNewDrillDescription(drill?.drill_description || "");
    setNewDrillTimed(drill?.is_timed || false);
    const seededSections = Array.isArray(drill?.development_sections)
      ? drill.development_sections.filter(Boolean)
      : [];
    const primarySection = seededSections[0] ? [seededSections[0]] : [];
    setSelectedSectionsForDrill(primarySection);
    setSelectedCategoriesForDrill(getDrillCategoryIds(drill));
    setSectionPickerExpanded(primarySection.length === 0);
    setCategorySearch("");
    setModalStep(1);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setNewDrillName("");
    setNewDrillTarget("");
    setNewDrillAttempts("");
    setNewDrillDescription("");
    setNewDrillTimed(false);
    setSelectedPositionsForDrill([]);
    setSelectedSectionsForDrill([]);
    setSelectedCategoriesForDrill([]);
    setSectionPickerExpanded(true);
    setCategorySearch("");
    setModalStep(1);
  };

  const saveDrillAndPositions = async () => {
    const isSkill = modalType === "skill";
    const table = isSkill ? "program_goal_skill_scores" : "program_goal_combine_scores";

    const payload = {
      program_id: selectedProgramId,
      target_score: newDrillTarget,
      drill_description: newDrillDescription || null,
      position_ids: selectedPositionsForDrill,
      development_sections: selectedSectionsForDrill,
      performance_categories: selectedCategoriesForDrill,
      is_timed: newDrillTimed,
    };

    if (isSkill) {
      payload.skill_name = newDrillName;
      payload.total_attempts = newDrillAttempts === "" ? null : parseInt(newDrillAttempts, 10);
    } else {
      payload.drill_name = newDrillName;
    }

    try {
      if (!currentDrillId) {
        const { data, error } = await supabase.from(table).insert([payload]).select().single();
        if (error) throw error;
        if (isSkill) setSkillScores((prev) => [...prev, data]);
        else setCombineScores((prev) => [...prev, data]);
      } else {
        const { error } = await supabase.from(table).update(payload).eq("id", currentDrillId);
        if (error) throw error;
        const updateState = (prev) =>
          prev.map((d) => (d.id === currentDrillId ? { ...d, ...payload } : d));
        if (isSkill) setSkillScores(updateState);
        else setCombineScores(updateState);
      }
      closeModal();
    } catch (error) {
      console.error("Critical Error saving data:", error.message);
      alert(`Save failed: ${error.message}`);
    }
  };

  const deleteDrill = async (id, type) => {
    const table =
      type === "skill" ? "program_goal_skill_scores" : "program_goal_combine_scores";
    await supabase.from(table).delete().eq("id", id);
    if (type === "skill") setSkillScores((prev) => prev.filter((d) => d.id !== id));
    else setCombineScores((prev) => prev.filter((d) => d.id !== id));
  };

  // --- Derived values ---
  const positionsForProgram = selectedProgramSportId
    ? positions.filter((p) => String(p.sport_id || "") === String(selectedProgramSportId))
    : positions.filter(
        (p) => p.sport?.name === programs.find((pr) => pr.id === selectedProgramId)?.sport
      );

  const sectionNameById = Object.fromEntries(
    (developmentSections || []).map((row) => [String(row.id), row.name])
  );
  const categoryNameById = Object.fromEntries(
    (performanceCategories || []).map((row) => [String(row.id), row.name])
  );

  const visibleDevelopmentSections = developmentSections || [];

  const visibleSectionIdSet = new Set(
    visibleDevelopmentSections.map((section) => String(section.id))
  );
  const selectedSectionId = String(selectedSectionsForDrill?.[0] || "").trim();

  const categoriesForSelectedSection = (performanceCategories || []).filter(
    (row) =>
      getCategorySectionId(row) === selectedSectionId &&
      visibleSectionIdSet.has(getCategorySectionId(row))
  );
  const visibleCategoriesForSection = categoriesForSelectedSection.filter((row) =>
    String(row.name || "")
      .toLowerCase()
      .includes(String(categorySearch || "").toLowerCase().trim())
  );

  useEffect(() => {
    if (!selectedSectionId) return;
    const hasCategoriesForSelectedSection = (performanceCategories || []).some(
      (row) => getCategorySectionId(row) === selectedSectionId
    );
    if (hasCategoriesForSelectedSection) return;

    let isCancelled = false;
    async function fetchCategoriesForSelectedSection() {
      let fetchedRows = [];
      let fetchError = null;

      const { data: byDevelopmentSectionId, error: byDevelopmentSectionIdError } = await supabase
        .from("performance_categories")
        .select("id, name, development_section_id")
        .eq("development_section_id", selectedSectionId)
        .order("name", { ascending: true });

      if (!byDevelopmentSectionIdError) {
        fetchedRows = byDevelopmentSectionId || [];
      } else {
        const { data: bySectionId, error: bySectionIdError } = await supabase
          .from("performance_categories")
          .select("id, name, section_id")
          .eq("section_id", selectedSectionId)
          .order("name", { ascending: true });
        if (!bySectionIdError) {
          fetchedRows = (bySectionId || []).map((row) => ({
            ...row,
            development_section_id: row.section_id,
          }));
        } else {
          fetchError = bySectionIdError;
        }
      }

      if (isCancelled) return;
      if (fetchError) {
        console.error(fetchError);
        return;
      }
      if (!Array.isArray(fetchedRows) || fetchedRows.length === 0) return;

      setPerformanceCategories((prev) => {
        const byId = new Map((prev || []).map((row) => [String(row.id), row]));
        fetchedRows.forEach((row) => byId.set(String(row.id), row));
        return Array.from(byId.values());
      });
    }

    fetchCategoriesForSelectedSection();
    return () => {
      isCancelled = true;
    };
  }, [selectedSectionId, performanceCategories]);

  useEffect(() => {
    if (!selectedSectionId) return;
    if (visibleSectionIdSet.has(selectedSectionId)) return;
    setSelectedSectionsForDrill([]);
    setSelectedCategoriesForDrill([]);
    setSectionPickerExpanded(true);
  }, [modalType, selectedSectionId, developmentSections]);

  const StopwatchBadge = () => (
    <div
      className="flex items-center gap-1 bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded-full border border-yellow-200"
      title="This drill is timed"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="12"
        height="12"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <circle cx="12" cy="12" r="10" />
        <polyline points="12 6 12 12 16 14" />
      </svg>
      <span className="text-[10px] font-bold uppercase tracking-wide">Timed</span>
    </div>
  );

  return (
    <div className="p-6 max-w-7xl mx-auto font-sans">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-extrabold tracking-tight text-gray-900">
          Program Administration
        </h1>
        <button
          type="button"
          onClick={() => setIsTipsModalOpen(true)}
          className="w-6 h-6 flex items-center justify-center rounded-full border border-amber-300 bg-white text-xs font-black text-amber-700"
          aria-label="Open admin efficiency tips"
        >
          i
        </button>
      </div>

      <div className="mb-6">
        <label className="block mb-2 font-semibold text-gray-700">Select Program:</label>
        <select
          className="border border-gray-300 p-3 rounded-lg w-full max-w-md bg-white shadow-sm focus:ring-2 focus:ring-blue-500 outline-none"
          value={selectedProgramId || ""}
          onChange={(e) => setSelectedProgramId(e.target.value)}
        >
          {programs.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name} ({p.sport})
            </option>
          ))}
        </select>
      </div>

      {selectedProgramId && (
        <>
          <div className="flex flex-wrap gap-4 mb-8">
            <button
              className="bg-gray-900 hover:bg-gray-800 text-white font-semibold px-5 py-2.5 rounded-lg shadow-lg transition-all active:scale-95"
              onClick={() => openModal("skill")}
            >
              + Add Skill Drill
            </button>
            <button
              className="bg-blue-600 hover:bg-blue-500 text-white font-semibold px-5 py-2.5 rounded-lg shadow-lg transition-all active:scale-95"
              onClick={() => openModal("combine")}
            >
              + Add Combine Drill
            </button>
          </div>

          <div className="space-y-8">
            {[
              { title: "Skill Drills", data: skillScores, type: "skill" },
              { title: "Combine Drills", data: combineScores, type: "combine" },
            ].map(
              (section) =>
                section.data.length > 0 && (
                  <div key={section.title}>
                    <h2 className="text-2xl font-bold mb-4 text-gray-800 border-b border-gray-200 pb-2">
                      {section.title}
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {section.data.map((item) => {
                        const assignedPositions = (positions || [])
                          .filter((pos) => item.position_ids?.includes(pos.id))
                          .map((pos) => pos.name);
                        const assignedSections = (item.development_sections || [])
                          .map((id) => sectionNameById[String(id)])
                          .filter(Boolean);
                        const assignedCategories = getDrillCategoryIds(item)
                          .map((id) => categoryNameById[String(id)])
                          .filter(Boolean);

                        const getAbbreviation = (name) =>
                          name
                            .split(" ")
                            .map((word) => word[0])
                            .join("")
                            .toUpperCase();

                        return (
                          <div
                            key={item.id}
                            className="relative p-6 rounded-xl shadow-sm border border-gray-200 bg-white hover:shadow-xl transition-all duration-200 group flex flex-col h-full"
                          >
                            <div className="absolute top-4 right-4 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                              <button
                                onClick={() => openModal(section.type, item)}
                                className="text-gray-400 hover:text-blue-600 p-1"
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" />
                                </svg>
                              </button>
                              <button
                                onClick={() => deleteDrill(item.id, section.type)}
                                className="text-gray-400 hover:text-red-600 p-1"
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <line x1="18" y1="6" x2="6" y2="18" />
                                  <line x1="6" y1="6" x2="18" y2="18" />
                                </svg>
                              </button>
                            </div>

                            <h3 className="font-bold text-lg mb-1 pr-12">
                              {item.skill_name || item.drill_name}
                            </h3>
                            <p className="text-sm text-gray-500 mb-4 line-clamp-2 flex-grow">
                              {item.drill_description || "No description."}
                            </p>

                            <div className="flex items-center justify-between mb-4 bg-gray-50 p-2.5 rounded-lg border border-gray-100">
                              <div className="flex flex-col">
                                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">
                                  Goal
                                </span>
                                <span className="text-sm font-bold text-gray-900">
                                  {item.target_score}
                                  {section.type === "skill" && Number(item.total_attempts) > 0 && (
                                    <span className="text-gray-400 font-normal">
                                      {" "}/ {item.total_attempts}
                                    </span>
                                  )}
                                </span>
                              </div>
                              {item.is_timed && <StopwatchBadge />}
                            </div>

                            <div className="pt-3 border-t border-gray-50 mt-auto space-y-2">
                              <div className="flex items-start justify-between gap-2">
                                <span className="text-[9px] font-bold text-gray-300 uppercase tracking-widest mt-1">
                                  Tags:
                                </span>
                                <div className="flex flex-wrap gap-1 justify-end">
                                  {assignedSections.map((name, idx) => (
                                    <span
                                      key={`section-${idx}`}
                                      className="px-2 py-0.5 rounded text-[9px] font-bold bg-indigo-50 text-indigo-600 border border-indigo-100"
                                    >
                                      {name}
                                    </span>
                                  ))}
                                  {assignedCategories.map((name, idx) => (
                                    <span
                                      key={`category-${idx}`}
                                      className="px-2 py-0.5 rounded text-[9px] font-bold bg-emerald-50 text-emerald-600 border border-emerald-100"
                                    >
                                      {name}
                                    </span>
                                  ))}
                                </div>
                              </div>
                              <div className="flex items-start justify-between gap-2">
                                <span className="text-[9px] font-bold text-gray-300 uppercase tracking-widest mt-1">
                                  Positions:
                                </span>
                                <div className="flex flex-wrap gap-1 justify-end">
                                  {assignedPositions.map((name, idx) => (
                                    <span
                                      key={`pos-${idx}`}
                                      title={name}
                                      className="px-2 py-0.5 rounded text-[9px] font-bold bg-gray-50 text-gray-400 border border-gray-200"
                                    >
                                      {getAbbreviation(name)}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )
            )}
          </div>
        </>
      )}

      {isTipsModalOpen && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl max-h-[90vh] overflow-auto">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <h3 className="text-base font-black text-gray-900 tracking-tight">
                Admin Efficiency Tips
              </h3>
              <button
                type="button"
                onClick={() => setIsTipsModalOpen(false)}
                className="text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-100 rounded-full transition"
                aria-label="Close tips modal"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
            <div className="px-5 py-4">
              <p className="text-sm text-gray-600 mb-3">
                Design your drill library so staff can score quickly and compare athletes consistently.
              </p>
              <ul className="space-y-2 text-sm text-gray-800">
                <li>1. Keep drill names specific and unique so coaches never guess what to score.</li>
                <li>2. Use timed mode only for true time-based tests; keep everything else untimed for consistency.</li>
                <li>3. Tag each drill with one section and only the categories that drive coaching decisions.</li>
                <li>4. Assign positions intentionally to avoid bloated scorecards with irrelevant drills.</li>
                <li>5. Review target scores monthly and update only when athlete readiness data supports the change.</li>
              </ul>
            </div>
            <div className="px-5 py-4 border-t border-gray-100 flex justify-end">
              <button
                type="button"
                onClick={() => setIsTipsModalOpen(false)}
                className="px-4 py-2 rounded-lg text-xs font-bold bg-gray-900 text-white hover:bg-black transition"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* --- MODAL --- */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[92vh] flex flex-col overflow-hidden">
            {/* Header */}
            <div className="flex justify-between items-center px-6 py-4 border-b border-gray-100 bg-gray-50/50">
              <div>
                <h3 className="text-xl font-black text-gray-800 tracking-tight">
                  {currentDrillId ? "Edit Configuration" : "Create New Drill"}
                </h3>
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mt-1">
                  Step {modalStep} of 2
                </p>
              </div>
              <div className="flex items-center gap-3">
                {currentDrillId && (
                  <div
                    className="max-w-[320px] px-3 py-1.5 rounded-lg bg-white border border-gray-200 text-right"
                    title={newDrillName || "Untitled Drill"}
                  >
                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">
                      Editing
                    </p>
                    <p className="text-xs font-bold text-gray-800 truncate">
                      {newDrillName || "Untitled Drill"}
                    </p>
                  </div>
                )}
                <button
                  onClick={closeModal}
                  className="text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-100 rounded-full transition"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-4 grid grid-cols-12 gap-3 bg-gradient-to-b from-slate-50/60 to-white overflow-y-auto">
              {modalStep === 1 ? (
                <>
                  {/* Drill Basics */}
                  <div className="col-span-12 lg:col-span-6 rounded-xl border border-blue-100 bg-blue-50/30 p-3">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs font-black text-blue-700 uppercase tracking-wider">
                        Step 1: Drill Basics
                      </p>
                      <span className="px-2 py-1 rounded bg-white border border-blue-100 text-[11px] font-bold text-blue-700">
                        {modalType === "skill" ? "Skill Drill" : "Combine Drill"}
                      </span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
                      <div className="md:col-span-12">
                        <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">
                          Drill Name
                        </label>
                        <input
                          type="text"
                          className="w-full bg-white border border-blue-100 rounded-lg p-2.5 text-sm focus:ring-2 focus:ring-blue-500 outline-none font-medium"
                          placeholder="e.g., Around the World"
                          value={newDrillName}
                          onChange={(e) => setNewDrillName(e.target.value)}
                          autoFocus
                        />
                      </div>
                      <div className="md:col-span-12">
                        <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">
                          Timing
                        </label>
                        <div
                          className="w-full flex items-center justify-between p-2.5 border border-blue-100 rounded-lg bg-white cursor-pointer"
                          onClick={() => setNewDrillTimed(!newDrillTimed)}
                        >
                          <div className="flex items-center gap-2">
                            <div
                              className={`p-1 rounded ${newDrillTimed ? "bg-yellow-100 text-yellow-600" : "bg-gray-200 text-gray-400"}`}
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="12" cy="12" r="10" />
                                <polyline points="12 6 12 12 16 14" />
                              </svg>
                            </div>
                            <span className={`text-sm font-bold ${newDrillTimed ? "text-gray-800" : "text-gray-400"}`}>
                              Timed Drill
                            </span>
                          </div>
                          <input
                            type="checkbox"
                            checked={newDrillTimed}
                            onChange={(e) => setNewDrillTimed(e.target.checked)}
                            className="w-4 h-4 accent-blue-600 rounded cursor-pointer"
                          />
                        </div>
                      </div>
                      <div className="md:col-span-12">
                        <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">
                          Description
                        </label>
                        <textarea
                          className="w-full bg-white border border-blue-100 rounded-lg p-3 text-sm focus:ring-2 focus:ring-blue-500 outline-none h-[110px] resize-none"
                          placeholder="Briefly explain the drill..."
                          value={newDrillDescription}
                          onChange={(e) => setNewDrillDescription(e.target.value)}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Targets & Positions */}
                  <div className="col-span-12 lg:col-span-6 rounded-xl border border-violet-100 bg-violet-50/30 p-3">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs font-black text-violet-700 uppercase tracking-wider">
                        Step 2: Targets & Coverage
                      </p>
                      <span className="px-2 py-1 rounded bg-white border border-violet-100 text-[11px] font-bold text-violet-700">
                        Positions: {selectedPositionsForDrill.length || 0}
                      </span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
                      <div className="md:col-span-3">
                        <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">
                          Target
                        </label>
                        <input
                          type="number"
                          step={newDrillTimed ? "0.1" : "1"}
                          inputMode={newDrillTimed ? "decimal" : "numeric"}
                          className="w-full bg-white border border-violet-100 rounded-lg p-2.5 text-sm focus:ring-2 focus:ring-blue-500 outline-none font-bold text-gray-800"
                          placeholder="0"
                          value={newDrillTarget}
                          onChange={(e) => setNewDrillTarget(e.target.value)}
                        />
                      </div>
                      {modalType === "skill" && (
                        <div className="md:col-span-3">
                          <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">
                            Attempts
                          </label>
                          <input
                            type="number"
                            className="w-full bg-white border border-violet-100 rounded-lg p-2.5 text-sm focus:ring-2 focus:ring-blue-500 outline-none font-bold text-gray-800"
                            placeholder="10"
                            value={newDrillAttempts}
                            onChange={(e) => setNewDrillAttempts(e.target.value)}
                          />
                        </div>
                      )}
                      <div className="md:col-span-12">
                        <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
                          Positions
                        </label>
                        <div className="flex flex-wrap gap-1.5 content-start max-h-24 overflow-y-auto pr-1">
                          {positionsForProgram.map((pos) => (
                            <button
                              key={pos.id}
                              onClick={() => togglePositionForDrill(pos.id)}
                              className={`px-2.5 py-1 rounded text-[10px] font-bold uppercase border transition-all ${
                                selectedPositionsForDrill.includes(pos.id)
                                  ? "bg-blue-600 text-white border-blue-600 shadow-sm"
                                  : "bg-white text-gray-500 border-gray-200 hover:border-gray-300"
                              }`}
                            >
                              {pos.abbreviation}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                /* Step 2: Categorization */
                <div className="col-span-12 rounded-xl border border-indigo-100 bg-indigo-50/30 p-3">
                  <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                    <p className="text-xs font-black text-indigo-700 uppercase tracking-wider">
                      Drill Categorization
                    </p>
                    <div className="flex items-center gap-2 text-[11px] font-bold">
                      <span className="px-2 py-1 rounded bg-white border border-indigo-100 text-indigo-700">
                        Section: {selectedSectionId ? 1 : 0}
                      </span>
                      <span className="px-2 py-1 rounded bg-white border border-emerald-100 text-emerald-700">
                        Categories: {selectedCategoriesForDrill.length}
                      </span>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                    {/* Section picker */}
                    <div className="bg-white border border-indigo-100 rounded-lg p-2.5">
                      <div className="flex items-center justify-between mb-2">
                        <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider">
                          Step 1: Development Section
                        </label>
                        {selectedSectionId && !sectionPickerExpanded && (
                          <button
                            type="button"
                            onClick={() => setSectionPickerExpanded(true)}
                            className="text-[10px] font-bold text-indigo-600 hover:text-indigo-700"
                          >
                            Change
                          </button>
                        )}
                      </div>
                      {selectedSectionId && !sectionPickerExpanded ? (
                        <button
                          type="button"
                          onClick={() => setSectionPickerExpanded(true)}
                          className="px-2.5 py-1 rounded text-[11px] font-bold border bg-indigo-600 text-white border-indigo-600 shadow-sm"
                        >
                          {sectionNameById[selectedSectionId] || "Selected Section"}
                        </button>
                      ) : (
                        <div className="grid grid-cols-2 gap-1.5">
                          {visibleDevelopmentSections.map((section) => (
                            <button
                              key={section.id}
                              onClick={() => toggleSectionForDrill(section.id)}
                              className={`px-2.5 py-1 rounded text-[11px] font-bold border transition-all ${
                                selectedSectionsForDrill.includes(String(section.id))
                                  ? "bg-indigo-600 text-white border-indigo-600 shadow-sm"
                                  : "bg-white text-gray-500 border-gray-200 hover:border-gray-300"
                              }`}
                            >
                              {section.name}
                            </button>
                          ))}
                          {visibleDevelopmentSections.length === 0 && (
                            <p className="text-[11px] text-gray-400 italic col-span-full">
                              No sections found for this sport.
                            </p>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Performance Categories picker */}
                    <div className="bg-white border border-emerald-100 rounded-lg p-2.5">
                      <div className="flex items-center justify-between mb-2">
                        <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider">
                          Step 2: Performance Categories
                        </label>
                        {selectedCategoriesForDrill.length > 0 && (
                          <button
                            type="button"
                            onClick={() => setSelectedCategoriesForDrill([])}
                            className="text-[10px] font-bold text-emerald-600 hover:text-emerald-700"
                          >
                            Clear
                          </button>
                        )}
                      </div>
                      <input
                        type="text"
                        value={categorySearch}
                        onChange={(e) => setCategorySearch(e.target.value)}
                        placeholder="Search categories..."
                        disabled={!selectedSectionId}
                        className="w-full mb-2 bg-gray-50 border border-gray-200 rounded-lg p-1.5 text-[11px] focus:ring-2 focus:ring-emerald-500 outline-none disabled:bg-gray-100 disabled:text-gray-400"
                      />
                      <div className="grid grid-cols-2 gap-1.5 max-h-28 overflow-y-auto pr-1">
                        {visibleCategoriesForSection.map((category) => (
                          <button
                            key={category.id}
                            onClick={() => toggleCategoryForDrill(category.id)}
                            className={`px-2.5 py-1 rounded text-[11px] font-bold border transition-all text-left ${
                              selectedCategoriesForDrill.includes(category.id)
                                ? "bg-emerald-600 text-white border-emerald-600 shadow-sm"
                                : "bg-white text-gray-500 border-gray-200 hover:border-gray-300"
                            }`}
                          >
                            {category.name}
                          </button>
                        ))}
                        {!selectedSectionId && (
                          <p className="text-[11px] text-gray-400 italic col-span-full">
                            Select a section first.
                          </p>
                        )}
                        {selectedSectionId && visibleCategoriesForSection.length === 0 && (
                          <p className="text-[11px] text-gray-400 italic col-span-full">
                            No categories match your search.
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-4 py-3 bg-white border-t border-gray-100 flex justify-end gap-2 sticky bottom-0">
              <button
                className="px-4 py-2 rounded-lg text-xs font-bold text-gray-500 hover:bg-gray-50 transition"
                onClick={closeModal}
              >
                Cancel
              </button>
              {modalStep === 2 && (
                <button
                  className="px-4 py-2 rounded-lg text-xs font-bold text-gray-700 border border-gray-200 hover:bg-gray-50 transition"
                  onClick={() => setModalStep(1)}
                >
                  Back
                </button>
              )}
              {modalStep === 1 ? (
                <button
                  className="px-4 py-2 rounded-lg text-xs font-bold bg-gray-900 text-white hover:bg-black shadow-lg shadow-gray-900/20 transition"
                  onClick={() => setModalStep(2)}
                >
                  Next
                </button>
              ) : (
                <button
                  className="px-4 py-2 rounded-lg text-xs font-bold bg-gray-900 text-white hover:bg-black shadow-lg shadow-gray-900/20 transition"
                  onClick={saveDrillAndPositions}
                >
                  Save Drill
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
